import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "src/components/common/Logo";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.classList.add("legal-page-route");
    document.body.classList.add("legal-page-route");

    return () => {
      document.documentElement.classList.remove("legal-page-route");
      document.body.classList.remove("legal-page-route");
    };
  }, []);

  return (
    <div className="min-h-screen w-full bg-white text-[#111827]">
      <header className="border-b border-[#e5e7eb] bg-white">
        <div className="mx-auto flex max-w-screen-lg items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 text-lg font-semibold text-[#111827]">
            <Logo />
            <span>EatsBits</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-[#FD7657] hover:underline">
            Owner Login
          </Link>
        </div>
      </header>

      <main className="w-full px-4 py-6 sm:px-6 md:px-10">
        <div className="container mx-auto max-w-screen-lg p-4 sm:p-6 lg:p-8">
          <h1 className="mb-6 text-center text-2xl font-bold sm:text-3xl">
            Privacy Policy
          </h1>
          <p className="mb-8 text-center text-sm text-gray-500">
            Effective Date: March 23, 2026
          </p>

          <section className="mb-6 mt-8">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              1. Introduction
            </h2>
            <p>
              Welcome to EatsBits. EatsBits provides restaurant technology,
              online ordering, enterprise meal ordering, and related meal
              program services, which may include restaurant management tools,
              point-of-sale and ordering features, workplace lunch programs, and
              community meal subscriptions. We value your privacy and are
              committed to protecting your personal information. By using our
              platform and services, you agree to the practices described in
              this Privacy Policy and our{" "}
              <a href="/terms/" className="text-blue-500 underline">
                Terms of Service
              </a>
              .
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              2. Information We Collect
            </h2>
            <p>
              To provide our platform and services, we may collect the following
              types of information:
            </p>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <strong>Personal Information:</strong> Name, email address,
                phone number, account credentials, and contact details for
                restaurant operators, enterprise administrators, employees,
                diners, subscribers, and other users.
              </li>
              <li>
                <strong>Business and Restaurant Information:</strong>
                Restaurant name, address, business contact information, tax or
                billing details, menus, operating hours, fulfillment settings,
                staff or account user information, and other information needed
                to configure and operate EatsBits services.
              </li>
              <li>
                <strong>Order and Meal Program Information:</strong> Items
                ordered, special instructions, restaurant selections, pickup,
                dine-in, delivery, catering, enterprise lunch, group meal, or
                community subscription preferences, participant selections,
                eligibility details, and order history.
              </li>
              <li>
                <strong>Payment Information:</strong> Payment-related
                information needed to complete transactions, manage restaurant
                plans or subscriptions, process meal program payments, issue
                refunds, and maintain transaction records. Payment card
                information is typically collected and processed by our
                third-party payment processors rather than stored directly by
                EatsBits, except as needed for transaction records and related
                business purposes.
              </li>
              <li>
                <strong>Usage Data:</strong> Information about how you interact
                with our platform, including pages visited, features used,
                device information, IP address, browser type, and approximate
                location derived from IP.
              </li>
              <li>
                <strong>Location Information:</strong> If you use location-based
                features, we may collect precise or approximate location
                information with your consent.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              3. How We Use Your Information
            </h2>
            <p>We use the collected information for the following purposes:</p>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <strong>To Provide Services:</strong> To create and manage your
                account, onboard restaurants and organizations, configure menus
                and ordering settings, process orders and meal subscriptions,
                facilitate checkout, manage enterprise or community meal
                programs, send confirmations, and provide customer support.
              </li>
              <li>
                <strong>To Communicate with You:</strong> To send
                service-related communications, including order confirmations,
                order status updates, pickup or delivery notifications, meal
                program notices, account notices, billing notices, and responses
                to support requests.
              </li>
              <li>
                <strong>SMS Messaging:</strong> If you provide your phone number
                during checkout or otherwise consent, we may send transactional
                SMS messages related to your order, account, restaurant
                operation, enterprise lunch, group meal, or community meal
                subscription. Message frequency varies. Message and data rates
                may apply. You may opt out at any time by replying STOP or reply
                HELP for assistance.
              </li>
              <li>
                <strong>To Improve Our Platform:</strong> To understand usage
                patterns, troubleshoot issues, improve features, and enhance the
                user experience.
              </li>
              <li>
                <strong>For Legal Compliance:</strong> To meet legal
                obligations, enforce our Terms of Service, and resolve disputes.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              4. Sharing Your Information
            </h2>
            <p>We may share your information in the following ways:</p>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <strong>With Restaurant Partners:</strong> To fulfill orders and
                transmit order or meal program information, including your name,
                phone number, order details, participant selections, eligibility
                details, and related instructions.
              </li>
              <li>
                <strong>With Enterprise and Community Program Administrators:</strong>{" "}
                When applicable, we may share information with an employer,
                organization, sponsor, or program administrator to operate an
                enterprise lunch, group meal, or community meal subscription,
                including participation status, order details, program usage,
                billing, eligibility, and support information.
              </li>
              <li>
                <strong>With Service Providers:</strong> We may share your
                information with vendors that help us operate the platform, such
                as payment processors, hosting providers, analytics providers,
                customer support tools, email providers, and SMS service
                providers. These providers may access personal information only
                as needed to perform services on our behalf and are not
                permitted to use it for their own marketing or promotional
                purposes.
              </li>
              <li>
                <strong>For Legal Reasons:</strong> We may disclose your
                information if required by law, regulation, legal process, or
                governmental request, or when we believe disclosure is necessary
                to protect our rights, users, partners, or the public.
              </li>
              <li>
                <strong>Business Transfers:</strong> We may share or transfer
                information in connection with a merger, acquisition, financing,
                reorganization, or sale of assets.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              5. Cookies and Analytics
            </h2>
            <p>
              We may use cookies, similar tracking technologies, and analytics
              tools to operate the platform, remember your preferences,
              understand usage patterns, and improve our services. You may be
              able to control certain cookies through your browser settings.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              6. Data Retention
            </h2>
            <p>
              We retain personal information for as long as reasonably necessary
              to provide our services, maintain business and tax records,
              resolve disputes, enforce our agreements, and comply with legal
              obligations. Retention periods may vary depending on the type of
              information and the purposes for which it was collected.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              7. Data Security
            </h2>
            <p>
              We use reasonable measures to protect your personal information
              from unauthorized access, disclosure, and misuse. However, please
              note that no method of transmission or storage is 100% secure, and
              we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              8. SMS Communications
            </h2>
            <p>
              If you provide your phone number during checkout, account setup,
              restaurant onboarding, enterprise lunch participation, community
              meal subscription signup, or otherwise consent to receive text
              messages, EatsBits may send transactional SMS messages related to
              your account, orders, restaurant operations, meal programs, order
              confirmations, order status updates, pickup notifications, and
              delivery or fulfillment notices.
            </p>
            <ul className="mt-2 list-inside list-disc space-y-2">
              <li>Message frequency varies based on your activity.</li>
              <li>Message and data rates may apply.</li>
              <li>You may opt out at any time by replying STOP.</li>
              <li>You may reply HELP for assistance.</li>
            </ul>
            <p className="mt-2">
              We do not use transactional SMS order notifications for marketing
              purposes without your separate consent. For more information,
              please review our{" "}
              <a href="/terms/" className="text-blue-500 underline">
                Terms of Service
              </a>
              .
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              9. Mobile Messaging Privacy
            </h2>
            <p>
              EatsBits does not share, sell, rent, or disclose mobile opt-in
              data, consent records, or mobile numbers collected for SMS
              messaging with third parties or affiliates for their marketing or
              promotional purposes.
            </p>
            <p className="mt-2">
              Mobile opt-in data and consent information may be shared only with
              service providers that support delivery of transactional SMS
              messages on our behalf, and solely for that purpose.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              10. Your Rights and Choices
            </h2>
            <p>
              Depending on your location, you may have the following rights
              concerning your personal data:
            </p>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <strong>Access and Correction:</strong> You can access and
                update your account information at any time.
              </li>
              <li>
                <strong>Deletion:</strong> You can request that we delete your
                personal data, subject to certain conditions, by contacting us.
              </li>
              <li>
                <strong>Opt-Out:</strong> You can opt-out of receiving
                marketing communications by adjusting your preferences or
                following the unsubscribe instructions in emails.
              </li>
              <li>
                <strong>SMS Opt-Out:</strong> You can opt out of transactional
                SMS messages at any time by replying STOP. You can reply HELP
                for assistance.
              </li>
              <li>
                <strong>Data Portability:</strong> You may request a copy of
                your personal information in a structured, commonly used, and
                machine-readable format.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              11. Children&apos;s Privacy
            </h2>
            <p>
              EatsBits does not knowingly collect personal information from
              children under the age of 13. If we learn that we have
              inadvertently collected information from a child under 13, we will
              take steps to delete that information as soon as possible.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              12. Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. Any changes
              will be posted on this page with an updated effective date. Please
              review this policy periodically to stay informed about how we are
              protecting your information.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="mb-3 text-xl font-semibold sm:text-2xl">
              13. Contact Information
            </h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or
              our data practices, please contact us at:
            </p>
            <p>
              Email:{" "}
              <a href="mailto:support@eatsbits.com" className="text-blue-500 underline">
                support@eatsbits.com
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
